import {jsx} from 'panel';
import partition from 'lodash/partition';
import camelCase from 'lodash/camelCase';

import {isFirstTimeEventFilter} from 'common/widgets/property-filter-menu/models/utils';
import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {SectionItem} from 'common/components/items-menu/types';
import {ButtonSize} from 'common/components/button/constants';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {isXauMathType} from 'common/query-builder/query-builder/util';
import {BEHAVIOR_PROPERTIES} from 'common/query-builder/query-block/properties-menu/constants';

import {QueryEntrySectionEl} from '../row-types/base/query-entry-section-el';
import {
  EntryPart,
  EntrySectionUpdateEvent,
  FiltersOperator,
  FiltersOperatorDisplayLabels,
  QueryEntrySectionAttrs,
  QueryFilterEntry,
  QuerySuperEntry,
  RowLabelType,
  SelectorTypeDefinitions,
} from '../types';
import {getFilterEntriesFromFilters} from '../row-types/filter/utils';

/**
 * @todo [pablo] all of this stuff re: 1st time filters can be simplified by using a one-off filter div since
 * 1st time filters are not actionable in any way other than removing
 */
export function RowFilters({
  entry,
  index,
  onUpdate,
  filtersOperators,
  selectedFiltersOperator,
  eventNames,
  filters,
  sectionAttrs,
  extraClass,
  inlineCohortsOverride,
  projectHasCohorts,
  hideEvents,
  isBehaviorMode,
  isReadOnly,
  hasNewUI,
  nestedAttrs,
  placeholders,
  onAbortStage,
}: {
  entry: QuerySuperEntry;
  index: number;
  onUpdate: (
    rawFilters?: PropertyFilter[],
    filtersOperator?: FiltersOperator,
    queryReason?: QueryReason,
    ev?: EntrySectionUpdateEvent<QueryFilterEntry>,
  ) => void;
  filtersOperators?: FiltersOperator[];
  selectedFiltersOperator?: FiltersOperator;
  eventNames: string[];
  filters: PropertyFilter[];
  sectionAttrs?: Partial<QueryEntrySectionAttrs>;
  extraClass?: Record<string, boolean>;
  inlineCohortsOverride: Obj;
  projectHasCohorts: boolean;
  hideEvents: boolean;
  isBehaviorMode: boolean;
  isReadOnly: boolean;
  hasNewUI: boolean;
  nestedAttrs: Partial<QueryEntrySectionAttrs>;
  placeholders: [EntryPart, QuerySuperEntry];
  onAbortStage: () => void;
}) {
  const showPlaceholder =
    placeholders?.includes(EntryPart.Filters) || placeholders?.includes(EntryPart.FirstTimeFilters);
  const filtersOperator = entry.filtersOperator;
  const aggregationOperator = entry.aggregationOperator;
  selectedFiltersOperator ||= filtersOperator;
  const hasFirstTimeEventFilter = filters.some((filter) => isFirstTimeEventFilter(filter));
  const [firstTimeFilters, regularFilters] = partition(filters, isFirstTimeEventFilter);

  if (filters.length === 0 && !showPlaceholder) {
    return null;
  }

  const filterParts = {
    [EntryPart.Filters]: regularFilters,
    [EntryPart.FirstTimeFilters]: firstTimeFilters,
  };

  const handleFiltersUpdate = (
    ev: EntrySectionUpdateEvent<QueryFilterEntry>,
    source: EntryPart.Filters | EntryPart.FirstTimeFilters,
  ): void => {
    const updatedFilters = Object.values({
      ...filterParts,
      [source]: ev.detail.entries.map((entry) => entry.event),
    }).flat();

    onUpdate(
      updatedFilters,
      selectedFiltersOperator || FiltersOperator.And,
      ev.detail.queryReason || QueryReason.QbFilterUpdate,
      ev,
    );
  };

  const filtersEl = (source: EntryPart.Filters | EntryPart.FirstTimeFilters) => {
    const filterEntries = getFilterEntriesFromFilters(filterParts[source], selectedFiltersOperator);

    // hide filters if empty or not staging
    if (filterEntries.length === 0 && !placeholders?.includes(source)) {
      return null;
    }

    const conjunctionRenderer = ({index: filterIndex, entry: filterEntry}) =>
      (hasNewUI ? filterIndex === 0 && filterEntries.length > 1 : true) && (
        <RowFiltersPrefix
          filterIndex={filterIndex}
          filtersOperators={filtersOperators}
          filter={new PropertyFilter((filterEntry as QueryFilterEntry).event)}
          filtersOperator={selectedFiltersOperator || FiltersOperator.And}
          isReadOnlyMode={isReadOnly}
          forNewUI={hasNewUI}
          handleChange={(item) => onUpdate(filters, item.value, QueryReason.QbFilterUpdate)}
        />
      );
    return (
      <QueryEntrySectionEl
        key={`entry-${source}-section`}
        class={{'indent-filters': true, 'contained-section': true}}
        attrs={{
          ...nestedAttrs,
          'autoactivate-placeholders': true,
          elref: camelCase(`entry-${source}`) /* entryFilters or entryBehavioralFilters, depending on source */,
          'event-names': eventNames,
          'disable-add-entry': true,
          'can-rename-step': false,
          'behavior-mode': isBehaviorMode,
          'row-index': index,
          'row-types': [SelectorTypeDefinitions.Filter],
          'row-label-type': RowLabelType.None,
          'hide-events': hideEvents,
          'project-has-cohorts': projectHasCohorts,
          'inline-cohorts-override': inlineCohortsOverride,
          'disabled-items': isXauMathType(aggregationOperator)
            ? Array.from(BEHAVIOR_PROPERTIES.keys()).reduce((acc, key) => {
                acc[key] = true;
                return acc;
              }, {})
            : {},
          'row-draggable': hasNewUI,
          'row-drop-disabled': true,
          'track-proprties': {...(nestedAttrs[`track-properties`] || {}), sectionName: `inline-filter`},
          'disable-clone-entry': source === EntryPart.FirstTimeFilters,
          entries: filterEntries,
          ...sectionAttrs,
        }}
        props={{
          isStagingRow: placeholders?.includes(source) && {
            // include any partially staged filter
            ...placeholders[1],
            filtersOperator: selectedFiltersOperator || FiltersOperator.And,
          },
          /** renders the AND/OR switch for inline filters */
          prefixRenderer: !hasNewUI && conjunctionRenderer,
          suffixRenderer: hasNewUI && conjunctionRenderer,
        }}
        on={{
          update: (ev: EntrySectionUpdateEvent<QueryFilterEntry>) => handleFiltersUpdate(ev, source),
          abortStage: onAbortStage,
        }}
      />
    );
  };

  return (
    <div sel='.mp-query-entry-row.for-filters.has-row-separator' class={extraClass}>
      {filtersEl(EntryPart.Filters)}
      {hasFirstTimeEventFilter && filtersEl(EntryPart.FirstTimeFilters)}
    </div>
  );
}

export function RowFiltersPrefix({
  filtersOperator,
  filter,
  filterIndex,
  handleChange,
  forceDisabled,
  isReadOnlyMode,
  forBehaviors,
  filtersOperators,
  forNewUI,
}: {
  filtersOperator: FiltersOperator;
  filter: PropertyFilter;
  filterIndex: number;
  handleChange: (item: SectionItem) => void;
  forceDisabled?: boolean;
  isReadOnlyMode: boolean;
  forBehaviors?: boolean;
  filtersOperators?: FiltersOperator[];
  forNewUI?: boolean;
}) {
  let canSwitch = false;
  let label = ``;
  let operators: FiltersOperator[] = [];

  const forExtendedLogicalOperators =
    filtersOperators &&
    (filtersOperators.includes(FiltersOperator.AndAny) || filtersOperators.includes(FiltersOperator.OrAll));
  const isExtendedLogicalOperator =
    filtersOperator === FiltersOperator.AndAny || filtersOperator === FiltersOperator.OrAll;

  // behavior filter as first filter
  if (filter && isFirstTimeEventFilter(filter)) {
    label = `for the`;
    if (forNewUI) {
      return null;
    }
    // first non-behavior filter
  } else if (filterIndex === 0 && !forNewUI) {
    label = forBehaviors ? `who` : `where`;
  } else {
    label = FiltersOperatorDisplayLabels[filtersOperator];
    canSwitch = true;
    operators = filtersOperators || [FiltersOperator.And, FiltersOperator.Or];
  }

  // for sidebar QB we display `And all, Or any`
  let labelMappings = FiltersOperatorDisplayLabels;
  if (forNewUI && !forExtendedLogicalOperators) {
    label = null;
    labelMappings = {
      [FiltersOperator.And]: `And all`,
      [FiltersOperator.Or]: `Or any`,
    };
  }

  // and/or switch
  return (
    <div
      sel='.entry-selector-prefix'
      class={{'behavior-mode': forBehaviors, 'for-extended-operators': forExtendedLogicalOperators}}
    >
      {!(isExtendedLogicalOperator && filterIndex > 1) && (
        <SelectEl
          class={{'is-disabled': forceDisabled || !canSwitch}}
          attrs={{
            readonly: isReadOnlyMode || forceDisabled || !canSwitch,
            elref: `filtersOperatorSelect`,
            theme: forNewUI ? SelectTheme.Secondary : SelectTheme.Tertiary,
            'menu-width': 90,
            'always-show-caret': forNewUI,
            size: forNewUI ? ButtonSize.Small : ButtonSize.Medium,
            sections: [
              {
                items: operators.map(
                  (operator) =>
                    ({
                      isSelected: filtersOperator && operator === filtersOperator.toString(),
                      value: operator,
                      label: labelMappings[operator],
                    } as SectionItem),
                ),
              },
            ],
            ...(label
              ? {
                  'selected-option': label,
                }
              : {}),
          }}
          on={{change: (ev: CustomEvent) => handleChange(ev.detail.item as SectionItem)}}
        />
      )}
    </div>
  );
}
